/* eslint-disable @typescript-eslint/no-unused-vars */
import React from 'react';

import { FlexibleDiv } from '@components/formStyles';
import CollapsibleMultiSelectionFilter from '@components/ui/filters/MultiSelectionFilter/CollapsibleMultiSelectionFilter';
import ClearAllFilters from '@components/views/components/ClearAllFilters/ClearAllFilters';
import { isEmptyFilters } from '@components/views/utils';
import STATIC_ROUTES from '@data/staticRoutes.json';
import { CAR_RENTAL_FILTER_QUERY_PARAM_KEYS } from '@page/carRental/list/constants';
import PriceRangeSlider from '@shared/PriceRangeSlider';
import { FILTERS_QUERY_PARAMS } from '@constants/constants';
import { useAppRouter } from '@contexts/router';
import { useSearchParamsToQuery } from '@hooks/useSearchParamsToQuery';
import { getFilterByKey, getPriceRange } from '@domain/carRental/utils';
import clean from '@utils/cleanObject';
import { ListingFilterItemKey } from '@components/ui/filters/MultiSelectionFilter/types';
import { useSkipEffectOnMount } from '@hooks/lifecycle';
import { getSelectedFilterByKeyNameFromQuery } from '@utils/carRentalFilterUtils';
import { CarRentalListFilterProps } from '@domain/carRental/hooks';

import { SectionWrapperStyled } from '../Filters.styled';

const CarRentalListFilters = ({
  filters,
  setPrices,
  prices,
  fuelTypes,
  setFuelTypes,
  transmissionTypes,
  setTransmissionTypes,
  providers,
  setProviders,
  vehicleClasses,
  setVehicleClasses,
  doorCounts,
  setDoorCounts,
  seatCounts,
  setSeatCounts,
  luggageCounts,
  setLuggageCounts,
}: CarRentalListFilterProps) => {
  const query = useSearchParamsToQuery();
  const { push } = useAppRouter();

  const {
    priceRanges: [priceRangeMin, priceRangeMax],
  } = getPriceRange(filters);

  const handlePriceChange = ([min, max]: number[]) => {
    const { page, ...nextQuery } = query;
    setPrices([min, max]);

    push({
      pathname: STATIC_ROUTES.carRentalSearchPathPrefix,
      query: {
        ...nextQuery,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MIN]: min,
        [CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MAX]: max,
      },
    });
  };

  /**
   * Check all filters are empty
   * If all filters are empty, clear all filters button should be disabled
   * If any filter is not empty, clear all filters button should be enabled
   * @returns boolean
   */
  const checkEmptyFilters = () => {
    let isEmptyFilter = true;

    const isEmptyFiltersResult = isEmptyFilters(query);

    if (!isEmptyFiltersResult) {
      isEmptyFilter = false;
    }

    return isEmptyFilter;
  };

  const handleSelectedFilter = (name: string, value?: any) => {
    const { page, ...nextQuery } = query;

    push({
      pathname: STATIC_ROUTES.carRentalSearchPathPrefix,
      query: clean({
        ...nextQuery,
        [name]: value,
      }),
    });
  };

  const handleFuelTypeChange = (keys: ListingFilterItemKey[]) => {
    setFuelTypes(keys);

    handleSelectedFilter(CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.FUEL_TYPES, keys);
  };

  const handleTransmissionTypeChange = (keys: ListingFilterItemKey[]) => {
    setTransmissionTypes(keys);

    handleSelectedFilter(
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.TRANSMISSION_TYPES,
      keys
    );
  };

  const handleVehicleClassChange = (keys: ListingFilterItemKey[]) => {
    setVehicleClasses(keys);

    handleSelectedFilter(
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.VEHICLE_CLASSES,
      keys
    );
  };

  const handleProviderIdsChange = (keys: ListingFilterItemKey[]) => {
    setProviders(keys);

    handleSelectedFilter(CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PROVIDERS, keys);
  };

  const handleChangeDoorCounts = (keys: ListingFilterItemKey[]) => {
    setDoorCounts(keys);

    handleSelectedFilter(CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.DOOR_COUNTS, keys);
  };

  const handleChangeSeatCounts = (keys: ListingFilterItemKey[]) => {
    setSeatCounts(keys);

    handleSelectedFilter(CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SEAT_COUNTS, keys);
  };

  const handleChangeLuggageCounts = (keys: ListingFilterItemKey[]) => {
    setLuggageCounts(keys);

    handleSelectedFilter(
      CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.LUGGAGE_COUNTS,
      keys
    );
  };

  const handleClearAllFilters = () => {
    setFuelTypes([]);
    setTransmissionTypes([]);
    setVehicleClasses([]);
    setProviders([]);
    setDoorCounts([]);
    setSeatCounts([]);
    setLuggageCounts([]);
    setPrices(
      priceRangeMin && priceRangeMax ? [priceRangeMin, priceRangeMax] : []
    );

    const { page, ...nextQuery } = query;

    /**
     * Remove all filters from query
     */
    for (let i = 0; i < FILTERS_QUERY_PARAMS.length; i++) {
      delete nextQuery[FILTERS_QUERY_PARAMS[i]];
    }

    push({
      pathname: STATIC_ROUTES.carRentalSearchPathPrefix ?? '',
      query: nextQuery,
    });
  };

  useSkipEffectOnMount(() => {
    setFuelTypes(
      getSelectedFilterByKeyNameFromQuery(
        query,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.FUEL_TYPES
      )
    );
    setTransmissionTypes(
      getSelectedFilterByKeyNameFromQuery(
        query,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.TRANSMISSION_TYPES
      )
    );
    setVehicleClasses(
      getSelectedFilterByKeyNameFromQuery(
        query,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.VEHICLE_CLASSES
      )
    );

    setProviders(
      getSelectedFilterByKeyNameFromQuery(
        query,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PROVIDERS
      )
    );

    setDoorCounts(
      getSelectedFilterByKeyNameFromQuery(
        query,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.DOOR_COUNTS
      )
    );
    setSeatCounts(
      getSelectedFilterByKeyNameFromQuery(
        query,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SEAT_COUNTS
      )
    );
    setLuggageCounts(
      getSelectedFilterByKeyNameFromQuery(
        query,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.LUGGAGE_COUNTS
      )
    );

    const priceMin =
      parseInt(
        query?.[CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MIN] as string
      ) || prices[0];
    const priceMax =
      parseInt(
        query?.[CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PRICE_MAX] as string
      ) || prices[1];

    setPrices(priceMin && priceMax ? [priceMin, priceMax] : []);
  }, [query]);

  return (
    <div>
      <FlexibleDiv bottom={20}>
        <ClearAllFilters
          onClick={handleClearAllFilters}
          disabled={checkEmptyFilters()}
        />
      </FlexibleDiv>

      <SectionWrapperStyled>
        <PriceRangeSlider
          onChange={handlePriceChange}
          values={[
            +(prices?.[0] || 0) as number,
            +(prices?.[1] || 0) as number,
          ]}
          min={priceRangeMin}
          max={priceRangeMax}
        />
      </SectionWrapperStyled>

      {getFilterByKey(filters, CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.FUEL_TYPES)
        ?.length ? (
        <SectionWrapperStyled>
          <CollapsibleMultiSelectionFilter
            title='Yakıt Tipi'
            selectedItems={fuelTypes}
            data={getFilterByKey(
              filters,
              CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.FUEL_TYPES
            )}
            handleSelectedItems={handleFuelTypeChange}
            subTitleKey='count'
          />
        </SectionWrapperStyled>
      ) : null}

      {getFilterByKey(
        filters,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.TRANSMISSION_TYPES
      )?.length ? (
        <SectionWrapperStyled>
          <CollapsibleMultiSelectionFilter
            title='Vites Tipi'
            selectedItems={transmissionTypes}
            data={getFilterByKey(
              filters,
              CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.TRANSMISSION_TYPES
            )}
            handleSelectedItems={handleTransmissionTypeChange}
            subTitleKey='count'
          />
        </SectionWrapperStyled>
      ) : null}

      {getFilterByKey(
        filters,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.VEHICLE_CLASSES
      )?.length ? (
        <SectionWrapperStyled>
          <CollapsibleMultiSelectionFilter
            title='Araç Tipi'
            selectedItems={vehicleClasses}
            data={getFilterByKey(
              filters,
              CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.VEHICLE_CLASSES
            )}
            handleSelectedItems={handleVehicleClassChange}
            subTitleKey='count'
          />
        </SectionWrapperStyled>
      ) : null}

      {getFilterByKey(filters, CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PROVIDERS)
        ?.length ? (
        <SectionWrapperStyled>
          <CollapsibleMultiSelectionFilter
            title='Sağlayıcı Firma'
            selectedItems={providers}
            data={getFilterByKey(
              filters,
              CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.PROVIDERS
            )}
            handleSelectedItems={handleProviderIdsChange}
            subTitleKey='count'
            withValue
          />
        </SectionWrapperStyled>
      ) : null}

      {getFilterByKey(filters, CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.DOOR_COUNTS)
        ?.length ? (
        <SectionWrapperStyled>
          <CollapsibleMultiSelectionFilter
            title='Kapı Sayısı'
            selectedItems={doorCounts}
            data={getFilterByKey(
              filters,
              CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.DOOR_COUNTS
            )}
            handleSelectedItems={handleChangeDoorCounts}
            subTitleKey='count'
          />
        </SectionWrapperStyled>
      ) : null}

      {getFilterByKey(filters, CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SEAT_COUNTS)
        ?.length ? (
        <SectionWrapperStyled>
          <CollapsibleMultiSelectionFilter
            title='Koltuk Sayısı'
            selectedItems={seatCounts}
            data={getFilterByKey(
              filters,
              CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.SEAT_COUNTS
            )}
            handleSelectedItems={handleChangeSeatCounts}
            subTitleKey='count'
          />
        </SectionWrapperStyled>
      ) : null}

      {getFilterByKey(
        filters,
        CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.LUGGAGE_COUNTS
      )?.length ? (
        <SectionWrapperStyled>
          <CollapsibleMultiSelectionFilter
            title='Bagaj Sayısı'
            selectedItems={luggageCounts}
            data={getFilterByKey(
              filters,
              CAR_RENTAL_FILTER_QUERY_PARAM_KEYS.LUGGAGE_COUNTS
            )}
            handleSelectedItems={handleChangeLuggageCounts}
            subTitleKey='count'
          />
        </SectionWrapperStyled>
      ) : null}
    </div>
  );
};

export default CarRentalListFilters;
